export const transformYouTubeWatchUrl = (videoUrl: string): string | null => {
  if (!videoUrl.includes('youtube.com/watch')) return null;

  const videoId = videoUrl.split('v=')[1]?.split('&')[0];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const transformYouTubeShortUrl = (videoUrl: string): string | null => {
  if (!videoUrl.includes('youtu.be/')) return null;

  const videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const transformYouTubeShortsUrl = (videoUrl: string): string | null => {
  if (!videoUrl.includes('youtube.com/shorts/')) return null;

  const videoId = videoUrl.split('/shorts/')[1]?.split('?')[0]?.split('/')[0];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const transformYouTubeLiveUrl = (videoUrl: string): string | null => {
  if (!videoUrl.includes('youtube.com/live/')) return null;

  const videoId = videoUrl.split('/live/')[1]?.split('?')[0]?.split('/')[0];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

export const isYouTubeEmbedUrl = (videoUrl: string): boolean => {
  return videoUrl.includes('youtube.com/embed/');
};

const VIMEO_PLAYER_URL = 'https://player.vimeo.com/video';

/** Extracts the unlisted/private-video privacy hash from a Vimeo URL's query. */
const getVimeoQueryHash = (videoUrl: string): string | null => {
  try {
    return new URL(videoUrl).searchParams.get('h');
  } catch {
    return null;
  }
};

export const transformVimeoUrl = (videoUrl: string): string | null => {
  if (!videoUrl.includes('vimeo.com/') || videoUrl.includes('/player.js') || videoUrl.includes('/embed/')) {
    return null;
  }

  // Already an embeddable player URL: keep it intact so an explicit privacy hash
  // survives untouched.
  if (videoUrl.includes('player.vimeo.com/video/')) {
    return videoUrl;
  }

  // Public videos are addressed as `/<id>`; unlisted (privacy-hash) videos are
  // addressed as `/<id>/<hash>`. The player needs the numeric id and, for
  // unlisted videos, the hash as the `h` query parameter — without it Vimeo
  // refuses to play the video. Falling back to the raw vimeo.com URL would point
  // the iframe at the Vimeo website, which the browser cannot frame.
  // e.g. https://vimeo.com/1214374943/856a49a632
  //   -> https://player.vimeo.com/video/1214374943?h=856a49a632
  const match = videoUrl.match(/vimeo\.com\/(\d+)(?:\/([A-Za-z0-9]+))?(?:[?#]|$)/);
  if (!match) {
    return null;
  }

  const [, videoId, pathHash] = match;
  const hash = pathHash ?? getVimeoQueryHash(videoUrl);
  return hash ? `${VIMEO_PLAYER_URL}/${videoId}?h=${hash}` : `${VIMEO_PLAYER_URL}/${videoId}`;
};

const YOUTUBE_TRANSFORMS = [
  transformYouTubeWatchUrl,
  transformYouTubeShortUrl,
  transformYouTubeShortsUrl,
  transformYouTubeLiveUrl,
];

export const getEmbedUrl = (videoUrl: string): string => {
  if (isYouTubeEmbedUrl(videoUrl)) return videoUrl;

  for (const transform of YOUTUBE_TRANSFORMS) {
    const result = transform(videoUrl);
    if (result) return result;
  }

  const vimeo = transformVimeoUrl(videoUrl);
  if (vimeo) return vimeo;

  return videoUrl;
};
